import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  ExternalLink,
  Inbox,
  Plus,
  RefreshCw,
  Rss,
  Settings,
  Trash2,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FeedSourceTabs } from '@/components/feed-sources/feed-source-tabs';
import { useSession } from '@/lib/auth';
import {
  DEFAULT_MGR_SOURCE_ID,
  LANDING_SOURCE_ID,
  type MgrInboxItem,
  type UpdateMgrSourceInput,
  mgrErrorMessage,
  mgrInboxQueryOptions,
  mgrSourceQueryOptions,
  mgrSourcesListQueryOptions,
  useBulkImportMgrItems,
  useCreateMgrSource,
  useDeleteMgrSource,
  useImportMgrItem,
  useRefreshMgrFeed,
  useUpdateMgrSource,
} from '@/lib/feed-mgronline';
import { hasPermission } from '@/lib/permissions';
import { toast } from '@/components/ui/toaster';
import type { FeedSourceListItem } from '@news-cms/shared';

interface FeedInboxSearch {
  source?: string;
}

export const Route = createFileRoute('/_authenticated/feed-sources/')({
  validateSearch: (search: Record<string, unknown>): FeedInboxSearch => ({
    source: typeof search.source === 'string' ? search.source : undefined,
  }),
  component: FeedInboxPage,
});

function FeedInboxPage() {
  const { user } = useSession();
  const canManage = hasPermission(user, 'feed.manage');
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // The default source row is auto-created by the backend on first load;
  // additional rows (e.g. category-filtered) are admin-seeded via SQL.
  const sourcesQuery = useQuery(mgrSourcesListQueryOptions);
  const sourceList = sourcesQuery.data ?? [];
  const activeSourceId = search.source ?? LANDING_SOURCE_ID;
  const activeSource = sourceList.find((s) => s.id === activeSourceId);
  const query = useQuery(mgrInboxQueryOptions(activeSourceId));
  const refresh = useRefreshMgrFeed(activeSourceId);
  const importItem = useImportMgrItem(activeSourceId);
  const bulkImport = useBulkImportMgrItems(activeSourceId);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Jump to a source tab by id (clearing ?source= for the landing tab).
  const goToSource = (id: string) => {
    void navigate({ search: { source: id === LANDING_SOURCE_ID ? undefined : id } });
  };

  // Drop the selection whenever the active source tab changes so ids from
  // one feed don't leak into a bulk import against another.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeSourceId]);

  if (!canManage) {
    return <div className="p-8 text-center text-sm text-slate-500">ไม่มีสิทธิ์เข้าถึง feed</div>;
  }

  const handleImport = (item: MgrInboxItem) => {
    setPendingId(item.id);
    importItem.mutate(item.id, {
      onSuccess: (result) => {
        setPendingId(null);
        void navigate({ to: '/posts/$postId', params: { postId: result.postId } });
      },
      onError: () => setPendingId(null),
    });
  };

  const items = query.data ?? [];
  const selectableIds = items.filter((i) => i.status === 'new').map((i) => i.id);
  const newCount = selectableIds.length;
  const activeSourceLabel = activeSource?.name ?? 'Manager Online';

  // Effective selection = picked ids still importable (a refetch may have
  // flipped some to "imported" out from under the checkbox state).
  const effectiveSelected = selectableIds.filter((id) => selectedIds.has(id));
  const selectedCount = effectiveSelected.length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
  };

  const handleBulkImport = () => {
    if (effectiveSelected.length === 0) return;
    bulkImport.mutate(
      { ids: effectiveSelected },
      {
        onSuccess: (summary) => {
          setSelectedIds(new Set());
          const parts = [`นำเข้า ${summary.imported} ข่าว`];
          if (summary.alreadyImported > 0) {
            parts.push(`ข้าม ${summary.alreadyImported} (นำเข้าแล้ว)`);
          }
          if (summary.failed.length > 0) parts.push(`ล้มเหลว ${summary.failed.length}`);
          const msg = `${parts.join(' · ')} — เป็นร่าง (เลือกหมวดก่อนเผยแพร่)`;
          if (summary.failed.length > 0) toast.error(msg);
          else toast.success(msg);
        },
        onError: (e) => toast.error(mgrErrorMessage(e)),
      },
    );
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <Rss className="text-brand-600 h-6 w-6" />
              Feed Inbox
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              วัตถุดิบจาก {activeSourceLabel} ({newCount} รอ import จาก {items.length} รายการ)
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              อัปเดตอัตโนมัติทุก 5 นาที · เก็บไว้ 24 ชั่วโมงแล้วลบ (รายการที่ import แล้วเก็บไว้ต่อ)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              เพิ่มแหล่ง
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              <Settings className="mr-1.5 h-3.5 w-3.5" />
              ตั้งค่า
            </Button>
            <Button
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending || query.isFetching}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${refresh.isPending || query.isFetching ? 'animate-spin' : ''}`}
              />
              ดึงใหม่
            </Button>
          </div>
        </div>

        {sourceList.length > 1 && (
          <FeedSourceTabs
            sources={sourceList}
            activeSourceId={activeSourceId}
            onSelect={goToSource}
          />
        )}

        {(query.isError || importItem.isError) && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{mgrErrorMessage(query.error ?? importItem.error)}</AlertDescription>
          </Alert>
        )}

        {selectableIds.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = selectedCount > 0 && !allSelected;
                }}
                onChange={toggleAll}
              />
              เลือกทั้งหน้า ({selectedCount}/{selectableIds.length})
            </label>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleBulkImport}
                disabled={selectedCount === 0 || bulkImport.isPending}
              >
                {bulkImport.isPending ? (
                  <>
                    <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" /> กำลังนำเข้า{' '}
                    {selectedCount} ข่าว
                  </>
                ) : (
                  <>
                    <Download className="mr-1.5 h-4 w-4" /> นำเข้า {selectedCount} ข่าวที่เลือก
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <Inbox className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ยังไม่มีรายการใน feed</p>
            <p className="text-xs text-slate-400">
              รอ cron poll ครั้งถัดไป (ทุก 5 นาที) หรือกด "ดึงใหม่"
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3">
            {items.map((item) => (
              <FeedRow
                key={item.id}
                item={item}
                pending={pendingId === item.id}
                selected={selectedIds.has(item.id)}
                onToggleSelect={() => toggleOne(item.id)}
                onImport={() => handleImport(item)}
                onOpenExisting={() =>
                  item.importedPostId &&
                  void navigate({
                    to: '/posts/$postId',
                    params: { postId: item.importedPostId },
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>

      <MgrSourceSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        sourceId={activeSourceId}
        canDelete={activeSourceId !== DEFAULT_MGR_SOURCE_ID && activeSourceId !== LANDING_SOURCE_ID}
        onDeleted={() => {
          toast.success('ลบแหล่งแล้ว');
          goToSource(LANDING_SOURCE_ID);
        }}
      />

      <MgrSourceCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(src) => {
          toast.success(`เพิ่มแหล่ง "${src.name}" แล้ว`);
          goToSource(src.id);
        }}
      />
    </div>
  );
}

interface RowProps {
  item: MgrInboxItem;
  pending: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onImport: () => void;
  onOpenExisting: () => void;
}

function FeedRow({ item, pending, selected, onToggleSelect, onImport, onOpenExisting }: RowProps) {
  const dt = item.publishedAt
    ? new Date(item.publishedAt).toLocaleString('th-TH', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  return (
    <li className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3 transition hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900">
      {item.status === 'new' && (
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 self-start"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`เลือก: ${item.topic}`}
        />
      )}
      <img
        src={item.thumbUrl || item.imageUrl}
        alt=""
        loading="lazy"
        className="h-20 w-28 shrink-0 rounded object-cover"
        // Some thumbs 404 — fall back to a transparent placeholder so the
        // row layout doesn't break.
        onError={(e) => {
          (e.target as HTMLImageElement).style.visibility = 'hidden';
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          {item.categoryName && (
            <span
              className="bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 mt-0.5 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
              title="หมวดจาก Manager Online"
            >
              {item.categoryName}
            </span>
          )}
          <div className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-slate-50">
            {item.topic}
          </div>
        </div>
        <div className="mt-1 line-clamp-2 text-xs text-slate-500">{item.intro}</div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
          <span>{dt}</span>
          {item.status === 'imported' ? (
            <button
              type="button"
              onClick={onOpenExisting}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300"
            >
              <CheckCircle2 className="h-3 w-3" />
              นำเข้าแล้ว · เปิดบทความ <ExternalLink className="h-3 w-3" />
            </button>
          ) : (
            <Button size="sm" onClick={onImport} disabled={pending}>
              {pending ? (
                <>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังนำเข้า
                </>
              ) : (
                <>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> นำเข้าเป็นร่าง
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

/* ---------- Settings dialog ---------- */

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceId: string;
  /** False for the default `src-mgronline` row (protected from deletion). */
  canDelete: boolean;
  onDeleted: () => void;
}

function MgrSourceSettingsDialog({
  open,
  onOpenChange,
  sourceId,
  canDelete,
  onDeleted,
}: SettingsDialogProps) {
  // Source row + detail URL drive the form prefill; only fetched while the
  // dialog is open so closing/reopening always shows the freshest server-side
  // values.
  const sourceQuery = useQuery({ ...mgrSourceQueryOptions(sourceId), enabled: open });
  const update = useUpdateMgrSource(sourceId);
  const del = useDeleteMgrSource();

  const [url, setUrl] = useState('');
  const [detailUrl, setDetailUrl] = useState('');
  const [name, setName] = useState('');
  const [interval, setInterval] = useState('10');
  const [isActive, setIsActive] = useState(true);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Drop the delete confirmation whenever the dialog toggles.
  useEffect(() => {
    if (!open) setConfirmDelete(false);
  }, [open]);

  const handleDelete = () => {
    del.mutate(sourceId, {
      onSuccess: () => {
        onOpenChange(false);
        onDeleted();
      },
      onError: (e) => setSubmitError(e),
    });
  };

  // Reset form fields whenever a fresh source row arrives.
  useEffect(() => {
    if (sourceQuery.data) {
      const { source, detailUrl: serverDetailUrl } = sourceQuery.data;
      setUrl(source.url);
      setDetailUrl(serverDetailUrl);
      setName(source.name);
      setInterval(String(source.fetchIntervalMinutes));
      setIsActive(source.isActive);
      setSubmitError(null);
    }
  }, [sourceQuery.data]);

  const handleSave = () => {
    if (!sourceQuery.data) return;
    setSubmitError(null);
    const { source: original, detailUrl: originalDetailUrl } = sourceQuery.data;
    const patch: UpdateMgrSourceInput = {};
    const trimmedUrl = url.trim();
    const trimmedDetailUrl = detailUrl.trim();
    const trimmedName = name.trim();
    const parsedInterval = Number(interval);
    if (trimmedUrl && trimmedUrl !== original.url) patch.url = trimmedUrl;
    if (trimmedDetailUrl && trimmedDetailUrl !== originalDetailUrl) {
      patch.detailUrl = trimmedDetailUrl;
    }
    if (trimmedName && trimmedName !== original.name) patch.name = trimmedName;
    if (
      Number.isFinite(parsedInterval) &&
      parsedInterval !== original.fetchIntervalMinutes &&
      parsedInterval >= 5 &&
      parsedInterval <= 1440
    ) {
      patch.fetchIntervalMinutes = parsedInterval;
    }
    if (isActive !== original.isActive) patch.isActive = isActive;

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    update.mutate(patch, {
      onSuccess: () => onOpenChange(false),
      onError: (e) => setSubmitError(e),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>ตั้งค่า Manager Online feed</DialogTitle>
          <DialogDescription>
            URL ต้องคืน JSON shape ของ mgronline (<code>{'{ news: [...] }'}</code>) — ถ้า shape
            ไม่ตรงจะเห็น error ใน "ข้อผิดพลาดล่าสุด"
          </DialogDescription>
        </DialogHeader>

        {sourceQuery.isLoading ? (
          <div className="py-8 text-center text-sm text-slate-500">กำลังโหลด...</div>
        ) : sourceQuery.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{mgrErrorMessage(sourceQuery.error)}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="mgr-url">URL feed list (q.php)</Label>
              <Input
                id="mgr-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://mjson.mgronline.com/q.php"
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-[11px] text-slate-400">
                คืน JSON ของรายการข่าวล่าสุด ใช้สำหรับ cron poll + รายการในหน้านี้
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mgr-detail-url">URL รายละเอียดข่าว (c.php)</Label>
              <Input
                id="mgr-detail-url"
                value={detailUrl}
                onChange={(e) => setDetailUrl(e.target.value)}
                placeholder="https://mjson.mgronline.com/c.php"
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-[11px] text-slate-400">
                ใช้ตอนกด "นำเข้าเป็นร่าง" — ดึง body HTML + ผู้เขียน + รูปภาพทั้งหมด
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mgr-name">ชื่อ</Label>
              <Input
                id="mgr-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="mgr-interval">รอบ poll (นาที)</Label>
                <Input
                  id="mgr-interval"
                  type="number"
                  min={5}
                  max={1440}
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                />
                <p className="text-[11px] text-slate-400">5 - 1440 นาที</p>
              </div>
              <div className="space-y-1.5">
                <Label className="block">สถานะ</Label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4"
                  />
                  เปิดใช้งาน (cron poll)
                </label>
              </div>
            </div>

            {sourceQuery.data && (
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <div>
                  Poll ล่าสุด:{' '}
                  {sourceQuery.data.source.lastPolledAt
                    ? new Date(sourceQuery.data.source.lastPolledAt).toLocaleString('th-TH')
                    : '—'}
                </div>
                {sourceQuery.data.source.lastError && (
                  <div className="mt-1 text-rose-600 dark:text-rose-400">
                    ข้อผิดพลาดล่าสุด: {sourceQuery.data.source.lastError}
                  </div>
                )}
              </div>
            )}

            {submitError !== null && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{mgrErrorMessage(submitError)}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <div>
            {canDelete &&
              (confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-rose-600 dark:text-rose-400">ลบถาวร?</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDelete(false)}
                    disabled={del.isPending}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={del.isPending}
                  >
                    {del.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : 'ลบถาวร'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:text-rose-700 dark:text-rose-400"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> ลบแหล่ง
                </Button>
              ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleSave}
              disabled={update.isPending || sourceQuery.isLoading || sourceQuery.isError}
            >
              {update.isPending ? (
                <>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
                </>
              ) : (
                'บันทึก'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Create dialog ---------- */

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (source: FeedSourceListItem) => void;
}

function MgrSourceCreateDialog({ open, onOpenChange, onCreated }: CreateDialogProps) {
  const create = useCreateMgrSource();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [interval, setInterval] = useState('10');
  const [submitError, setSubmitError] = useState<unknown>(null);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setName('');
      setUrl('');
      setInterval('10');
      setSubmitError(null);
    }
  }, [open]);

  const handleCreate = () => {
    setSubmitError(null);
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName || !trimmedUrl) {
      setSubmitError('กรอกชื่อและ URL feed');
      return;
    }
    const parsedInterval = Number(interval);
    create.mutate(
      {
        name: trimmedName,
        url: trimmedUrl,
        fetchIntervalMinutes:
          Number.isFinite(parsedInterval) && parsedInterval >= 5 && parsedInterval <= 1440
            ? parsedInterval
            : undefined,
      },
      {
        onSuccess: (source) => {
          onOpenChange(false);
          onCreated(source);
        },
        onError: (e) => setSubmitError(e),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>เพิ่มแหล่ง feed (tab ใหม่)</DialogTitle>
          <DialogDescription>
            สร้างแท็บใหม่จาก URL q.php ของ Manager Online (มักมี query หมวด เช่น{' '}
            <code>?cat=86</code>). URL รายละเอียด (c.php) ใช้ค่ากลางร่วมทุกแหล่ง —
            ไม่ต้องกรอกที่นี่.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-mgr-name">ชื่อ tab</Label>
            <Input
              id="new-mgr-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="เช่น การเมือง, กีฬา"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-mgr-url">URL feed list (q.php)</Label>
            <Input
              id="new-mgr-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://mjson.mgronline.com/q.php?cat=86"
              spellCheck={false}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-mgr-interval">รอบ poll (นาที)</Label>
            <Input
              id="new-mgr-interval"
              type="number"
              min={5}
              max={1440}
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="w-32"
            />
            <p className="text-[11px] text-slate-400">5 - 1440 นาที (ค่าเริ่มต้น 10)</p>
          </div>

          {submitError !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {typeof submitError === 'string' ? submitError : mgrErrorMessage(submitError)}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            ยกเลิก
          </Button>
          <Button onClick={handleCreate} disabled={create.isPending}>
            {create.isPending ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังเพิ่ม
              </>
            ) : (
              <>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> เพิ่มแหล่ง
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
